/**
 * Browser Notification API wrapper for workflow "step complete" alerts.
 *
 * Kept as plain functions (not a hook) so any layer — mutation callbacks,
 * cron poller results, socket handlers — can fire from wherever. React
 * components that need to react to permission changes use
 * `getNotificationPermission()` inside a state initializer.
 */

export type NotifyPermission = 'default' | 'granted' | 'denied' | 'unsupported';

function isSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function getNotificationPermission(): NotifyPermission {
  if (!isSupported()) return 'unsupported';
  return Notification.permission as NotifyPermission;
}

/**
 * Prompt the user for notification permission. Idempotent — returns the
 * cached permission if the user already answered.
 */
export async function requestNotificationPermission(): Promise<NotifyPermission> {
  if (!isSupported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const result = await Notification.requestPermission();
    return result as NotifyPermission;
  } catch {
    return 'default';
  }
}

interface FireOpts {
  body?: string;
  /** Groups notifications so a repeat step doesn't stack; latest replaces prior. */
  tag?: string;
  /** URL to navigate to when the user clicks the notification. Absolute or app-relative. */
  href?: string;
  /** Icon URL (data: URIs work too). Defaults to the page's declared favicon. */
  icon?: string;
  /** Auto-close after this many ms. Some OSes ignore this. Default 8000. */
  autoCloseMs?: number;
}

/**
 * Look up the icon the page already declares (index.html
 * <link rel="icon">) so the OS notification matches the browser tab
 * without us having to ship a duplicate asset.
 */
function pageIconUrl(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  return link?.href;
}

/**
 * Fire a browser (system) notification. No-op if permission isn't granted
 * or the API isn't available. Never throws — silent on failure.
 */
export function fireNotification(title: string, opts: FireOpts = {}): void {
  if (!isSupported()) return;
  if (Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, {
      body: opts.body,
      tag: opts.tag,
      icon: opts.icon ?? pageIconUrl(),
      // requireInteraction keeps the toast on-screen until the user
      // dismisses it — otherwise Chrome auto-collapses in ~5s. The user
      // asked for the notification to be visible in the system tray, so
      // this is the right default for step-complete alerts.
      requireInteraction: true,
    } as NotificationOptions);
    if (opts.href) {
      n.onclick = (e) => {
        e.preventDefault();
        window.focus();
        window.location.href = opts.href!;
        n.close();
      };
    }
    const autoClose = opts.autoCloseMs ?? 0;
    if (autoClose > 0) {
      setTimeout(() => {
        try { n.close(); } catch { /* already closed */ }
      }, autoClose);
    }
  } catch {
    // Notification constructor can throw on mobile or when the tab isn't
    // top-level; swallow so app flow isn't interrupted.
  }
}
